import FullCalendar from "@fullcalendar/react";
import { DateSpanApi, EventApi } from "@fullcalendar/core"
import { IAvailability } from "../interfaces/resource";

// Keep the calendar ref shape small so we can pass it easily.
type CalendarRef = { current: FullCalendar | null }

// Reuse a single RAF handle for marker painting.
let markerRaf: number | null = null
let lastMarkerKey: string | null = null
let lastResourceKey: string | null = null
// Cache previous availability per resource to avoid repainting all rows.
const lastAvailabilityByResource = new Map<string, EEventStatus>()
const resourceRowById = new Map<string, HTMLDivElement>()

const eventAllowed = (dateSpan: DateSpanApi,
    checkShiftLocationIsValid: (start: Date, end: Date, eventId?: string, resourceId?: string, originalResourceId?: string) => EEventStatus,
    calendarRef: CalendarRef,
    event: EventApi | null,
    defineUserAvailability: (dateStart: Date, dateEnd: Date, resourceID: string, eventId: string) => IAvailability[]
): boolean => {
    const originalResource = event?.getResources()[0]
    if (!originalResource) {
        return false
    }
    const eventId = event?.id ?? ''
    // Build a key so we don't repaint markers with identical inputs.
    const markerKey = `${eventId}|${originalResource.id}|${dateSpan.start.toISOString()}|${dateSpan.end.toISOString()}`

    // eventAllow fires extremely frequently while dragging; keep the marker painting work
    // out of the hot path to avoid jank (missing drag mirror) during rapid drag/drop.
    if (markerKey !== lastMarkerKey) {
        lastMarkerKey = markerKey

        if (markerRaf != null) {
            cancelAnimationFrame(markerRaf)
        }

        // Defer the DOM work to the next frame for smoother drag.
        markerRaf = requestAnimationFrame(() => {
            const availability = defineUserAvailability(dateSpan.start, dateSpan.end, originalResource.id, eventId)
            const resourceView: HTMLCollectionOf<Element> = document.getElementsByClassName('fc-resource')

            // Build a map of resource ID -> rendered row element
            const entries: Array<[string, HTMLDivElement]> = []
            for (let i = 0; i < resourceView.length; i++) {
                const resource = resourceView.item(i) as HTMLDivElement | null
                if (!resource) continue
                const currentResource = resource.dataset.resourceId
                if (!currentResource || currentResource === '0') continue
                entries.push([currentResource, resource])
            }

            // Only update resource row map if the set of rendered resources changed (avoids unnecessary lookups)
            const resourceKey = entries.map(([id]) => id).join('|')
            if (resourceKey !== lastResourceKey) {
                resourceRowById.clear()
                entries.forEach(([id, el]) => resourceRowById.set(id, el)) // Update resource key
                lastResourceKey = resourceKey
            }

            // Only repaint rows whose availability actually changed.
            availability.forEach((av) => {
                const prev = lastAvailabilityByResource.get(av.resourceId)
                if (prev === av.availability) return
                lastAvailabilityByResource.set(av.resourceId, av.availability)

                const el = resourceRowById.get(av.resourceId)
                if (el) {
                    el.style.background = setMarkerColor(av.availability)
                }
            })
        })
    }

    const allowed = checkShiftLocationIsValid(dateSpan.start, dateSpan.end, event?.id, dateSpan.resource?.id, originalResource.id)
    return allowed !== EEventStatus.error
    // return true
}


const eventDragStop = (calendarRef: CalendarRef) => {
    // Reset any in-flight marker work once the drag ends.
    lastMarkerKey = null
    lastResourceKey = null
    if (markerRaf != null) {
        cancelAnimationFrame(markerRaf)
        markerRaf = null
    }
    lastAvailabilityByResource.clear()
    resourceRowById.clear()

    const resourceView: HTMLCollection = document.getElementsByClassName('fc-resource')
    for (let i = 0; i < resourceView.length; i++) {
        const res = resourceView[i] as HTMLDivElement
        if (res) {
            if (res.dataset.resourceId !== '0') {
                res.style.background = 'white'
            }

        }
    }
}

const setMarkerColor = (allowed: EEventStatus): string => {
    switch (allowed) {
        case EEventStatus.error:
            return 'rgba(255,142,142,0.3)'
        case EEventStatus.valid:
            return 'rgba(242,242,242,0.3)'
        case EEventStatus.characteristicsIssue:
            return 'rgba(245,172,24,0.3)'
    }
}

export enum EEventStatus {
    valid,
    characteristicsIssue,
    error
}

export { eventAllowed, eventDragStop }
